// Generate Ads wizard — three-step state machine driving the
// campaign → products → generate flow.
//
// Settings (templates / CTA / tracking URL) was dropped from the wizard
// in favor of AI-driven variety: all ai_* templates run by default per
// (media × product) seed, copy_candidates feed the CTA text at render
// time, and ctaUrl falls back to brand.websiteUrl. Operators no longer
// pick templates or author CTA copy in the wizard — the four
// CreativeDirector concepts per media are what drive output variety.
//
// Step state lives in the URL (?step=campaign|products|generate) so
// back/forward and sharable bookmarks work. Selections live in React
// state and are passed to each step as props.
//
// Routes that fold into this wizard via empty states:
//   no campaigns → Step 1 offers Connect / New Campaign / Upload Media
//   no media     → Step 2 redirects to /upload (deep-link route preserved)
//
// Legacy ?step=settings URLs from older bookmarks fall through to
// 'campaign' via the STEPS.some() guard below — the type keeps
// 'settings' as a valid (but unused) key so TS doesn't complain on
// stale links until they get rewritten by the next save.

import { useEffect, useMemo, useRef, useState } from 'react';
import type { MouseEvent, ReactNode } from 'react';
import { useSearchParams, Link as RouterLink } from 'react-router-dom';
import {
  Box, HStack, VStack, Button, Text, Heading, Flex, Badge, Select,
  Switch, Textarea, Accordion, AccordionItem, AccordionButton, AccordionPanel,
  AccordionIcon, useToast, Spinner
} from '@chakra-ui/react';
import { apiJson } from '../../auth/apiFetch';
import { useBrand } from '../../brand/BrandContext';
import { useAdReadiness } from '../../brand/useAdReadiness';
import { PageHeader } from '../../shell/PageHeader';
import { Step1Campaign } from './Step1Campaign';
import { Step2Picker } from './Step2Picker';
import { Step4Generate } from './Step4Generate';
import { readGenerateHandoff } from './handoff';
import type { FormatEntry } from './formatCatalog';
import { useVideoSpendQuote } from './useVideoSpendQuote';
import type { VideoQuoteContext, VideoSpendQuote } from './useVideoSpendQuote';
import {
  defaultSceneModelKey,
  formatSubtitle,
  imageVideoKinds,
  isFormatLive,
  useFormatCatalog
} from './formatCatalog';
import { WorkflowPicker } from './WorkflowPicker';
import { SceneReelOutputsCard } from './SceneReelOutputsCard';
import { SceneReelControls } from './SceneReelControls';
import { sceneReelBrandAllowed } from './sceneReelRequest';

// Inline picker — wizard-scoped, lives in the shell file. Cards are driven
// by GET /api/ads/formats (see formatCatalog.ts); coming_soon surfaces stay
// visible but greyed so Google/pmax can ship as a stub without being
// selectable. resolveKinds() on the backend still clamps nonsense combos.
//
// MULTI-SELECT: the cards are checkboxes, not radios, and there is no per-card
// 'both' kind any more — image and video are independent toggles, so "both" is
// simply both chips lit. State is the two key arrays on WizardSelections.
//
// The two sides are NOT symmetrical, because the two pipelines do not bill the
// same way, and the picker's job is to make that visible rather than hide it:
//   STATIC — free multi-select. Each ticked size is its own billable image
//     generation, because the image model typesets the ad copy INTO the pixels,
//     so a size can never be a crop of another size.
//   VIDEO — single-select WITHIN Meta (one 9:16 master per product, every other
//     Meta aspect cropped from it at titling), multi-select ACROSS platforms
//     (Google PMax bills two real masters, 9:16 + 16:9, because a landscape
//     cannot be cropped out of a vertical). Derive-only surfaces are not
//     offered at all — see VIDEO_DERIVE_ONLY.
// resolveExplicitFormats on the backend enforces all of this independently; the
// rules are mirrored here only so the picker can quote a truthful count.

// Video surfaces that are DERIVED FOR FREE from another master and must never be
// offered as a video selection. Mirrors the derive-only annotation on
// GOOGLE_VIDEO_FANOUT in services/platformFormats.js: PMax needs two real
// masters (9:16 + 16:9, since a landscape cannot be cropped out of a vertical),
// but its 1:1 IS a crop of the 9:16, so generating it separately would be a
// third billable Omni submit for something we get for nothing.
//
// This is a UI guard on top of a backend guard, not instead of one — the
// 'explicit' resolver strips these server-side too. It is here because leaving
// the chip clickable would let an operator tick a surface that silently
// contributes no video, which reads as a broken checkbox.
const VIDEO_DERIVE_ONLY: readonly string[] = ['pmax_video_1_1'];

// Derive-only video surface → the billable MASTER it is a free crop of.
// Backend rule (liquidretail_backend services/platformFormats.js
// PMAX_VIDEO_DERIVE_SOURCE / campaignAdsGenerationService.isGoogleVideoMasterRun):
// pmax_video_1_1 is never its own Omni submit — it is cropped for free from the
// settled pmax_video_9_16 master, and ONLY when that master is present in the
// same run. GET /api/ads/formats does not expose this dependency on the format
// entry (see FormatEntry in formatCatalog.ts — key/label/aspectRatio/
// deliveryDims/kinds/status/platform only, no derive/master field), so there is
// nothing in the catalog payload to read it from. Hardcoded to the single pair
// that exists today; if the API ever grows a `derivesFrom` field, read that
// instead of extending this map by hand.
const VIDEO_DERIVE_MASTER: Readonly<Record<string, string>> = {
  pmax_video_1_1: 'pmax_video_9_16',
  // Meta's fan-out went live 2026-08-11: the 9:16 master mints free 1:1 and
  // 4:5 crops plus a Reels retitle (backend META_VIDEO_DERIVATIVES). These are
  // NOT in VIDEO_DERIVE_ONLY — unlike the PMax square, each of these surfaces
  // is still independently generatable on its own (pick Meta 4:5 alone in
  // Advanced and it becomes its own billable master). They are free only when
  // the 9:16 master is what was bought, which is exactly what this map says.
  meta_feed_1_1:  'meta_stories_9_16',
  meta_feed_4_5:  'meta_stories_9_16',
  meta_reels_9_16: 'meta_stories_9_16'
};

// `platform` on a FormatEntry is only ever 'meta' | 'google' (stamped from the
// backend's per-platform block — see FormatEntry.platform's doc comment), but
// 'google' covers THREE separate Google ad products — PMax, Demand Gen, and
// YouTube Shorts — that the catalog does not otherwise distinguish (no
// `surface` field is exposed by GET /api/ads/formats' formatEntry()). So "All
// PMax …" cannot be derived from `platform` alone without also sweeping in
// Demand Gen / Shorts sizes the task never asked for.
//
// Every PMax key — live or coming_soon, static or video, including the frozen
// legacy pmax_16_9 — uses the `pmax_` prefix (services/platformFormats.js).
// This is a NAMING-CONVENTION filter on real catalog keys, not a hardcoded
// list of specific sizes: it grows/shrinks automatically as the backend adds
// or retires PMax surfaces, the same way the Meta filter below does.
const isPmaxFormat = (f: FormatEntry) => f.key.startsWith('pmax_');
const isMetaFormat = (f: FormatEntry) => f.platform === 'meta';

// Shared "every live surface of this kind matching `match`" filter — the same
// rule the original All-static/All-video presets used, generalized so the
// platform-scoped presets (All Meta …, All PMax …) can reuse it byte-for-byte
// instead of re-deriving the derive-only exclusion in four more places.
function liveFormatKeys(
  formats: FormatEntry[],
  kind: 'image' | 'video',
  match?: (f: FormatEntry) => boolean
): string[] {
  return formats
    .filter(f =>
      isFormatLive(f)
      && imageVideoKinds(f).includes(kind)
      && (kind !== 'video' || !VIDEO_DERIVE_ONLY.includes(f.key))
      && (!match || match(f))
    )
    .map(f => f.key);
}

// Is every key in `keys` currently selected? Empty `keys` (e.g. a platform
// with zero live surfaces of this kind) is never "on" — an empty preset
// button should read as off/disabled, not as trivially satisfied.
const isPresetFullySelected = (keys: string[], selected: Set<string>): boolean =>
  keys.length > 0 && keys.every(k => selected.has(k));

// ADD or REMOVE exactly `subset` from `current`, leaving everything else
// untouched. This is what makes the four platform-scoped preset buttons
// independently combinable — unlike "All static formats" / "All video
// formats", which deliberately REPLACE the whole axis (they mean "everything,
// full stop"), these four must be layerable with each other and with any
// manual ticks outside their own set.
function toggleKeySubset(current: string[], subset: string[], turnOn: boolean): string[] {
  if (turnOn) return Array.from(new Set([...current, ...subset]));
  const drop = new Set(subset);
  return current.filter(k => !drop.has(k));
}

// Exported so the UGC wizard (Emami-RS-Project/liquidretail
// pages/UgcWizard/index.tsx) can mount the same picker above its step
// body — same billable / preset semantics on both surfaces without a
// duplicate implementation.
// ── Platform sections ───────────────────────────────────────────────
//
// The picker is split by PLATFORM first and kind second, because that is how
// the spend actually partitions: Meta and PMax price video completely
// differently (one master vs two), so a control that spans both can only ever
// describe one of them honestly. Each section owns exactly two buttons —
// "All static" and "All video" — and all four are independently combinable,
// which is what makes "everything, everywhere" a four-click operation rather
// than a mode.
//
// `match` is how a section claims a catalog key. Meta reads the platform field
// the backend stamps; PMax needs the `pmax_` key prefix because `platform:
// 'google'` also covers Demand Gen and YouTube Shorts, which are separate ad
// products this section does not sell (see isPmaxFormat).
const PLATFORM_SECTIONS: ReadonlyArray<{
  id:    'meta' | 'pmax';
  label: string;
  note:  string;
  match: (f: FormatEntry) => boolean;
}> = [
  {
    id:    'meta',
    label: 'Meta',
    note:  'Instagram + Facebook',
    match: isMetaFormat
  },
  {
    id:    'pmax',
    label: 'PMax',
    note:  'Google Performance Max',
    match: isPmaxFormat
  }
];

// Aspect ratios behind a set of catalog keys, deduped and in catalog order.
// This is what "with sizes listed" means on a preset button: the operator sees
// the shapes they are buying, not just how many.
function aspectList(formats: FormatEntry[], keys: string[]): string[] {
  const seen = new Set<string>();
  const out: string[] = [];
  for (const k of keys) {
    const a = formats.find(f => f.key === k)?.aspectRatio;
    if (a && !seen.has(a)) { seen.add(a); out.push(a); }
  }
  return out;
}

// Meta's single billable video master. Mirrors META_VIDEO_MASTER in
// services/platformFormats.js — the surface resolveExplicitFormats collapses
// every Meta video tick down to. Named here (rather than sniffed by aspect)
// because the backend names it too, so the two stay comparable by grep.
const META_VIDEO_MASTER_KEY = 'meta_stories_9_16';

// Display mirror of the backend's per-platform video clamp
// (resolveExplicitFormats). Given the video keys a platform would contribute,
// return the ones that actually BILL, WITHIN that platform:
//   Meta  — ONE master however many aspects are ticked; 1:1 / 4:5 / Reels are
//     cropped from it at titling. Prefer the named master, else the first
//     alphabetically, which is the backend's own tie-break.
//   PMax  — every ticked master bills (9:16 and 16:9 are genuinely separate
//     submits; a landscape cannot be cropped out of a vertical), and the
//     derive-only 1:1 is already excluded upstream by liveFormatKeys.
// Keeping this in ONE function is the point: the badge count and the
// paid/free size list are then guaranteed to tell the same story, and there
// is a single place to fix if the backend rule ever moves.
//
// This is PER-PLATFORM only — it does not know about the other platform, so
// it is still correct for the per-platform "All video" badges (a PMax-only
// run really does bill 9:16 and 16:9 separately). It is NOT enough on its own
// for a COMBINED count; see countDistinctVideoPlates below for the case where
// both platforms are in the same run.
function clampBillableVideo(platform: 'meta' | 'pmax', keys: string[]): string[] {
  if (platform !== 'meta') return keys;
  if (keys.length <= 1) return keys;
  return [keys.includes(META_VIDEO_MASTER_KEY) ? META_VIDEO_MASTER_KEY : [...keys].sort()[0]];
}

// PMax's portrait master. Mirrors PMAX_VIDEO_DERIVE_SOURCE in
// services/campaignAdsGenerationService.js — named here for the same reason
// META_VIDEO_MASTER_KEY is: so the two stay comparable by grep.
const PMAX_PORTRAIT_MASTER_KEY = 'pmax_video_9_16';

// Canonical PLATE identity for one already-clamped billable video key, across
// platforms. Used ONLY by the Everything capstone badge, which is a
// catalog-level ceiling ("what you get if you tick all four"), not the
// operator's spend line. Sharing Meta 9:16 + PMax 9:16 is a backend
// conjunct (resolvePortraitMasterFormat) the catalog does not publish —
// collapsing them here under-quotes when any conjunct fails. The COMBINED
// HEADLINE is client-computed for Meta-only and PMax-only (unconditional
// per-product masters). Mixed Meta+PMax — the only conjunct-dependent
// shape — is sourced from POST /api/ads/preview billable.videoMasters,
// and names the 2-or-3 range until that quote is current. Do not wire
// this helper back into the spend line.
function videoPlateId(key: string): string {
  return key === META_VIDEO_MASTER_KEY || key === PMAX_PORTRAIT_MASTER_KEY
    ? 'shared_9_16'
    : key;
}

// Distinct billable PLATES across a combined selection — the question a
// mixed-platform run actually asks ("how many Omni masters does this mint"),
// as opposed to summing each platform's own clamped count, which
// double-counts the shared 9:16 plate the moment both platforms are in the
// same run. Pass each platform's own CLAMPED billable keys (i.e. already
// through clampBillableVideo) — this does not re-clamp within a platform,
// it only dedupes ACROSS platforms.
function countDistinctVideoPlates(billableKeysByPlatform: string[][]): number {
  return new Set(billableKeysByPlatform.flat().map(videoPlateId)).size;
}

// The aspects a video selection actually DELIVERS, split into what is paid
// for and what rides along free.
//
// ⚠️ "DELIVERS" MEANS AN AD ROW THAT EXISTS, NOT AN ASPECT WE COULD IN
// PRINCIPLE CROP. The distinction is the whole reason this function is not
// simply "every ticked surface":
//   Meta  — the backend queues ONE Ad, the 9:16 master
//     (campaignAdsGenerationService: "Other Meta video sizes remain Phase 3
//     derivations (not queued)"). The 1:1 / 4:5 / Reels crops are documented
//     INTENT, not built. So Meta delivers 9:16 and nothing else, and listing
//     its other aspects — free or otherwise — promises ads that never appear.
//   PMax  — 9:16 and 16:9 are separate submits, AND the derive-only 1:1 is
//     genuinely minted, but only when its master is in the run
//     (isGoogleVideoMasterRun requires pmax_video_9_16). So it is claimed as
//     free only when that master is actually selected.
//
// An earlier version of this function derived Meta's "free" aspects from its
// other live video surfaces, which made the button read
// "1:1 · 4:5 · 9:16 — 1:1, 4:5 cropped free". Symmetrical with PMax, and
// false. Two independent reviews caught it; the asymmetry is real and the UI
// has to carry it.
function videoDelivery(
  formats: FormatEntry[],
  platform: 'meta' | 'pmax',
  match: (f: FormatEntry) => boolean,
  billableKeys: string[]
): {
  deliveredAspects: string[];
  freeCrops: Array<{ aspect: string; from: string; display: string }>;
} {
  const paidKeys = clampBillableVideo(platform, billableKeys);
  // A surface ships FREE when the master it is cropped from is one of the
  // masters actually being paid for, and it is not itself a paid master.
  //
  // Keyed on the PAID set, not on VIDEO_DERIVE_ONLY, because the two platforms
  // reach "free" differently and only this framing covers both. PMax's square
  // is derive-only — never independently generatable. Meta's 1:1 / 4:5 / Reels
  // ARE independently generatable (pick Meta 4:5 alone in Advanced and it
  // becomes its own billable master); they are free only in the selection
  // where the 9:16 master is what was bought. Testing the paid set is exactly
  // that distinction, and it matches the backend's own gate — the derivatives
  // are minted only when META_VIDEO_DERIVE_SOURCE is in masterFormats.
  const freeKeys = formats
    .filter(f =>
      isFormatLive(f)
      && match(f)
      && imageVideoKinds(f).includes('video')
      && !paidKeys.includes(f.key)
      && paidKeys.includes(VIDEO_DERIVE_MASTER[f.key])
    )
    .map(f => f.key);
  const paid = aspectList(formats, paidKeys);
  // Each free shape carries the shape it is cropped FROM. Naming the source
  // matters because it is not guessable: the PMax square comes off the 9:16
  // master specifically, so a 16:9-only run yields no square — "cropped free"
  // without a source reads as "free with any video", which is wrong.
  //
  // A derivative whose SHAPE already appears in the paid list is still its own
  // delivered creative and must not be dropped. Meta's Reels retitle is 9:16 —
  // the same shape as the Stories master it derives from — so the earlier
  // `!paid.includes(c.aspect)` filter silently swallowed it, and the button
  // advertised three shapes for a selection that ships four videos. Where the
  // shape alone is ambiguous, the surface's own name disambiguates it; that is
  // why `display` exists separately from `aspect` and is what the UI renders.
  const shown = new Set(paid);
  const freeCrops = freeKeys
    .map(k => {
      const f   = formats.find(x => x.key === k);
      const src = formats.find(x => x.key === VIDEO_DERIVE_MASTER[k]);
      return {
        aspect: f?.aspectRatio || '',
        from:   src?.aspectRatio || '',
        label:  (f?.label || '').replace(/^(Meta|Google)\s+/, '')
      };
    })
    .filter(c => c.aspect && c.from)
    .map(c => {
      const collides = shown.has(c.aspect);
      shown.add(c.aspect);
      return {
        aspect:  c.aspect,
        from:    c.from,
        display: collides && c.label ? `${c.aspect} ${c.label}` : c.aspect
      };
    });
  return {
    deliveredAspects: [...paid, ...freeCrops.map(c => c.display)],
    freeCrops
  };
}

// One preset button. Extracted because the four were previously four
// near-identical thirty-line blocks, and a styling or aria fix that lands in
// three of them is a bug that is invisible in review.
function PresetButton({
  label, sizes, count, countNoun, on, disabled, onToggle
}: {
  label:     string;
  sizes:     string;
  count:     number;
  countNoun: string;
  on:        boolean;
  disabled:  boolean;
  onToggle:  () => void;
}) {
  return (
    <Box
      as="button"
      type="button"
      onClick={() => { if (!disabled) onToggle(); }}
      flex="1 1 200px"
      textAlign="left"
      px={3}
      py={2}
      borderWidth="1px"
      borderColor={on ? 'rsViolet.500' : 'brand.border'}
      bg={on ? 'rsViolet.500' : 'white'}
      color={on ? 'white' : 'brand.ink'}
      borderRadius="md"
      cursor={disabled ? 'not-allowed' : 'pointer'}
      opacity={disabled ? 0.55 : 1}
      disabled={disabled || undefined}
      transition="border-color 0.12s, background 0.12s"
      aria-pressed={on}
    >
      <HStack spacing={2} justify="space-between" align="baseline">
        <Text fontSize="12px" fontWeight="800">{label}</Text>
        <Badge fontSize="9px" colorScheme={on ? 'whiteAlpha' : 'purple'} variant="subtle" flex="none">
          {count} {countNoun}{count === 1 ? '' : 's'}
        </Badge>
      </HStack>
      <Text fontSize="10px" mt={0.5} opacity={on ? 0.9 : 0.7} color={on ? 'white' : 'brand.muted'}>
        {sizes}
      </Text>
    </Box>
  );
}

function renderVideoSpend(quote: VideoSpendQuote): ReactNode {
  // NOTE: the word order "N or M" (not "M or N") is load-bearing for the
  // ui-smoke j2 journey, which parses this prose with
  // /(\d+) video generation/ — on "2 or 3 video generations per product"
  // the regex skips "2" (next token is "or") and captures "3", the correct
  // unshared count today. Reversing the order to "3 or 2" would make the
  // regex capture "2" and fail that assertion. Do not reorder this phrase.
  const perProductPhrase = (n: number) =>
    `${n} video generation${n === 1 ? '' : 's'} per product`;
  const mixedRangePhrase = (min: number, max: number) =>
    min === max ? perProductPhrase(min) : `${min} or ${max} video generation${max === 1 ? '' : 's'} per product`;

  if (quote.status === 'pending') {
    return (
      <>
        <Spinner size="xs" color="brand.muted" mr={1} display="inline-block" />
        <strong>{mixedRangePhrase(quote.minPerProduct, quote.maxPerProduct)}</strong>
        {' '}— checking whether the 9:16 master is shared.
      </>
    );
  }
  if (quote.status === 'blocked') {
    const need =
      quote.reason === 'no-products' ? 'needs at least one product'
      : quote.reason === 'no-templates' ? 'needs a template'
      : 'needs a campaign and at least one product';
    return (
      <>
        <strong>{mixedRangePhrase(quote.minPerProduct, quote.maxPerProduct)}</strong>
        {' '}— whether the 9:16 master is shared is not settled ({need}).
      </>
    );
  }
  if (quote.status === 'error') {
    return (
      <>
        <strong>{mixedRangePhrase(quote.minPerProduct, quote.maxPerProduct)}</strong>
        {' '}— whether the 9:16 master is shared is not settled (couldn&apos;t load the quote).
      </>
    );
  }
  if (quote.status === 'ready' && quote.perProduct != null) {
    return (
      <strong>
        {perProductPhrase(quote.perProduct)}
      </strong>
    );
  }
  if (quote.status === 'ready') {
    return (
      <strong>
        {quote.totalMasters} billable video master{quote.totalMasters === 1 ? '' : 's'}{' '}
        for {quote.productCount} product{quote.productCount === 1 ? '' : 's'}
      </strong>
    );
  }
  return null;
}

// Exported so the UGC wizard (Emami-RS-Project/liquidretail
// pages/UgcWizard/index.tsx) can mount the same picker above its step
// body — same billable / preset semantics on both surfaces without a
// duplicate implementation.
export function PlatformFormatPicker({
  staticKeys, videoKeys, onSelectionChange, formats, fromFallback, quoteContext
}: {
  staticKeys:        string[];
  videoKeys:         string[];
  onSelectionChange: (next: { staticKeys: string[]; videoKeys: string[] }) => void;
  formats:           FormatEntry[];
  fromFallback:      boolean;
  quoteContext?:      VideoQuoteContext;
}) {
  const staticSet = useMemo(() => new Set(staticKeys), [staticKeys]);
  const videoSet  = useMemo(() => new Set(videoKeys),  [videoKeys]);

  // STATIC is a true multi-select: each ticked surface is its own billable
  // image generation, so the operator picking two really does mean two.
  const toggleStatic = (key: string) => {
    const next = new Set(staticSet);
    if (next.has(key)) next.delete(key); else next.add(key);
    onSelectionChange({ staticKeys: [...next], videoKeys });
  };

  // VIDEO is single-select WITHIN Meta but multi-select ACROSS platforms, which
  // mirrors what each pipeline actually bills (resolveExplicitFormats):
  //   Meta   — ONE 9:16 master per product; 1:1, 4:5 and Reels are cropped from
  //     it. Letting two Meta surfaces light up would show two video ads and
  //     deliver one, so a new Meta tick REPLACES the previous Meta tick.
  //   Google — TWO real masters (9:16 + 16:9); a landscape cannot be cropped out
  //     of a vertical, so each is its own submit and they toggle independently.
  // Derive-only surfaces never reach here — canVideo excludes them.
  const platformOf = (k: string) => formats.find(f => f.key === k)?.platform;
  // Every video surface is an independent tick, Meta included.
  //
  // Meta video used to be single-select here: ticking one Meta aspect silently
  // unticked the others. That was a money guard standing in the wrong place. The
  // real guard is server-side and unconditional — resolveExplicitFormats
  // collapses ANY number of Meta video ticks to the single billable master
  // (META_VIDEO_MASTER), and expandWizardJob then mints 1:1, 4:5 and Reels as
  // FREE derivatives of it. So one Meta tick and four Meta ticks queue the same
  // one Omni submit; the clamp cannot be defeated from here.
  //
  // What the single-select actually cost was legibility: unticking the operator's
  // previous choice reads as a bug, and it hid the fact that Meta ships four
  // videos for one charge. The billing line under the picker states the paid
  // count, so multi-tick shows what is delivered without implying it is what is
  // billed. PMax is genuinely different — 9:16 and 16:9 are two real submits
  // because a landscape cannot be cropped out of a portrait — and it was already
  // multi-select, so this makes the two platforms behave alike.
  const toggleVideo = (key: string) => {
    const next = new Set(videoSet);
    if (next.has(key)) next.delete(key); else next.add(key);
    onSelectionChange({ staticKeys, videoKeys: [...next] });
  };

  // Per-platform live sets + on-states + billable counts. Only live surfaces
  // count: a coming_soon entry must never inflate a badge while the backend's
  // filterLiveFormats would silently drop it.
  const sections = useMemo(() => PLATFORM_SECTIONS.map(section => {
    const staticFormatKeys = liveFormatKeys(formats, 'image', section.match);
    const videoFormatKeys  = liveFormatKeys(formats, 'video', section.match);
    // BILLABLE video, through the same clamp the size list uses, so the badge
    // and the sizes can never disagree. Quoting videoFormatKeys.length for
    // Meta would over-state it fourfold. Keep the clamped KEYS (not just the
    // count) — countDistinctVideoPlates needs them to dedupe the shared 9:16
    // plate across sections for the combined ("Everything") count below.
    const videoBillableKeys = clampBillableVideo(section.id, videoFormatKeys);
    const videoBillable = videoBillableKeys.length;
    const delivery = videoDelivery(formats, section.id, section.match, videoFormatKeys);
    return {
      ...section,
      staticFormatKeys,
      videoFormatKeys,
      videoBillableKeys,
      videoBillable,
      delivery,
      cards:     formats.filter(section.match),
      staticOn:  isPresetFullySelected(staticFormatKeys, staticSet),
      videoOn:   isPresetFullySelected(videoFormatKeys,  videoSet)
    };
  }), [formats, staticSet, videoSet]);

  const anyVideoOn = videoKeys.length > 0;

  // ── "Everything" capstone (owner, 2026-08-11) ──────────────────────
  // An express mode for "all PMax static and video and all Meta static and
  // video" in one click, sitting above the per-platform blocks.
  //
  // Derived as the UNION of the SAME per-section subsets the four buttons
  // below already use — never a fifth re-derivation off the raw catalog — so
  // "everything" can never come to mean something different from what the
  // four individual buttons mean combined. Each section's video list already
  // excludes the derive-only square, so the free-crop rule (a PMax video
  // selection must include the 9:16 its 1:1 derives from) is inherited rather
  // than re-implemented.
  const everythingStatic = useMemo(
    () => Array.from(new Set(sections.flatMap(s => s.staticFormatKeys))),
    [sections]
  );
  const everythingVideo = useMemo(
    () => Array.from(new Set(sections.flatMap(s => s.videoFormatKeys))),
    [sections]
  );
  // "On" exactly when every section's two buttons are independently fully
  // selected, so this can never disagree with what those badges show.
  const everythingOn = sections.length > 0 && sections.every(s => s.staticOn && s.videoOn);
  const everythingHasAnything = everythingStatic.length > 0 || everythingVideo.length > 0;
  // Billable video across both platforms — deduped by PLATE across each
  // section's own clamped keys, not summed, so Everything's badge does not
  // double-count the shared 9:16 plate (see countDistinctVideoPlates). Both
  // sections' catalogs are live in full here (this is "every live surface",
  // not the current ticks), so this is the count an operator gets by turning
  // Everything on from a clean selection.
  const everythingVideoBillable = countDistinctVideoPlates(sections.map(s => s.videoBillableKeys));

  // BILLABLE video count across the whole selection, through the SAME clamp
  // the per-platform badges use — so the headline number and the two badges
  // can never tell different stories, and there is one function to fix if the
  // backend rule moves.
  //
  // Derive-only keys are excluded before counting. The UI cannot currently
  // put one in `videoKeys` (canVideo excludes them, and the derive-only card
  // toggles its MASTER instead), but counting one would over-state the bill
  // by a whole Omni submit against a backend that correctly refuses to bill
  // it — and a spend figure that is wrong upward is still wrong.
  // Per-platform badge sum — NOT the billed combined count for mixed.
  // Meta-only / PMax-only headlines reuse this number as a client rate.
  // Mixed uses it only as the unshared upper bound of the 2-or-3 range,
  // and to decide whether a ready preview quote is shared (sum - 1) or
  // unshared (sum). Never a settled mixed headline.
  const badgeVideoSum = useMemo(() => {
    const ticks = videoKeys.filter(k => !VIDEO_DERIVE_ONLY.includes(k));
    const meta   = clampBillableVideo('meta', ticks.filter(k => platformOf(k) === 'meta'));
    const google = clampBillableVideo('pmax', ticks.filter(k => platformOf(k) === 'google'));
    return meta.length + google.length;
  // platformOf closes over `formats`; listing it keeps the memo honest.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [videoKeys, formats]);

  // Which platforms the CURRENT selection actually involves — so the spend
  // summary describes the run in front of the operator rather than reciting
  // both platforms' pricing every time.
  const metaVideoTicked = videoKeys.some(k => platformOf(k) === 'meta');
  const pmaxVideoTicked = videoKeys.some(k => platformOf(k) === 'google' && !VIDEO_DERIVE_ONLY.includes(k));
  // The free PMax square exists only when the master it is cropped from is
  // in the run — isGoogleVideoMasterRun requires pmax_video_9_16 specifically,
  // so a 16:9-only selection ships no square.
  const pmaxSquareFree = videoKeys.includes(VIDEO_DERIVE_MASTER.pmax_video_1_1);
  // Same condition, different question: VIDEO_DERIVE_MASTER.pmax_video_1_1
  // IS PMAX_PORTRAIT_MASTER_KEY, so "pmax_video_9_16 is ticked" is exactly
  // pmaxSquareFree above — reused rather than re-tested so the two questions
  // ("is the free square live" / "is PMax's portrait master requested") can
  // never disagree. Combined with a Meta tick, this is exactly what PR #218's
  // resolvePortraitMasterFormat gates the shared 9:16 plate on (Meta master
  // minted + PMax portrait master requested, same run) — see
  // countDistinctVideoPlates, which is what actually bills this correctly.
  const sharedPortraitPlate = metaVideoTicked && pmaxSquareFree;
  // Does PMax ALSO have its own separate landscape master in this run? Only
  // matters once we know 9:16 is riding Meta's shared plate for free, to say
  // whether PMax bills anything of its own at all. Derived the same way
  // isPmaxFormat/liveFormatKeys derive PMax membership elsewhere in this
  // file — by asking which surface it is, not by naming pmax_video_16_9.
  const pmaxLandscapeTicked = videoKeys.some(k =>
    platformOf(k) === 'google' && k !== PMAX_PORTRAIT_MASTER_KEY && !VIDEO_DERIVE_ONLY.includes(k)
  );

  const videoQuote = useVideoSpendQuote(quoteContext, staticKeys, videoKeys, {
    sharingPossible: sharedPortraitPlate,
    unsharedPerProduct: badgeVideoSum
  });

  const sharingConfirmed =
    videoQuote.status === 'ready'
    && videoQuote.perProduct != null
    && sharedPortraitPlate
    && videoQuote.perProduct === badgeVideoSum - 1;

  return (
    <Box borderWidth="1px" borderColor="brand.border" borderRadius="lg" bg="brand.surface" p={4}>
      <HStack justify="space-between" align="baseline" mb={3}>
        <Text fontSize="11px" fontWeight="700" textTransform="uppercase" letterSpacing="0.06em" color="brand.muted">
          Platform format
        </Text>
        {(staticKeys.length > 0 || anyVideoOn) && (
          <Box
            as="button"
            type="button"
            onClick={() => onSelectionChange({ staticKeys: [], videoKeys: [] })}
            fontSize="10px"
            fontWeight="700"
            color="brand.muted"
            cursor="pointer"
          >
            Clear
          </Box>
        )}
      </HStack>

      {/* ── One block per platform ──────────────────────────────────────
          All four buttons are independently combinable (toggleKeySubset adds
          or removes only its own key subset), so selecting every one of them
          is a supported request rather than an accident: the backend's
          'explicit' preset partitions the ticks by platform and applies each
          platform's own video rule. */}
      {/* THE one-button express mode. Sits above the platform blocks because
          it is the shortcut past them, not a peer of them — an operator who
          wants everything should not have to read the split first. */}
      <Box
        as="button"
        type="button"
        onClick={() => { if (everythingHasAnything) onSelectionChange({
          staticKeys: toggleKeySubset(staticKeys, everythingStatic, !everythingOn),
          videoKeys:  toggleKeySubset(videoKeys,  everythingVideo,  !everythingOn)
        }); }}
        w="100%"
        textAlign="left"
        px={3}
        py={2}
        mb={3}
        borderWidth="1px"
        borderColor={everythingOn ? 'rsViolet.500' : 'brand.border'}
        bg={everythingOn ? 'rsViolet.500' : 'white'}
        color={everythingOn ? 'white' : 'brand.ink'}
        borderRadius="md"
        cursor={everythingHasAnything ? 'pointer' : 'not-allowed'}
        opacity={everythingHasAnything ? 1 : 0.55}
        disabled={!everythingHasAnything || undefined}
        transition="border-color 0.12s, background 0.12s"
        aria-pressed={everythingOn}
      >
        <HStack spacing={2} justify="space-between" align="baseline">
          <Text fontSize="12px" fontWeight="800">Everything (Meta + PMax)</Text>
          <Badge fontSize="9px" colorScheme={everythingOn ? 'whiteAlpha' : 'purple'} variant="subtle" flex="none">
            {everythingStatic.length} size{everythingStatic.length === 1 ? '' : 's'}
            {' · '}
            {everythingVideoBillable} master{everythingVideoBillable === 1 ? '' : 's'}
          </Badge>
        </HStack>
        <Text fontSize="10px" mt={0.5} opacity={everythingOn ? 0.9 : 0.7} color={everythingOn ? 'white' : 'brand.muted'}>
          Every live size on both platforms — same billing as ticking all four below
        </Text>
      </Box>

      <VStack align="stretch" spacing={3}>
        {sections.map(section => {
          const staticSizes = aspectList(formats, section.staticFormatKeys);
          const { deliveredAspects, freeCrops } = section.delivery;
          // Grouped by source so a multi-crop platform reads as
          // "1:1, 4:5 cropped free from 9:16" rather than repeating the source
          // once per shape. Meta derives three surfaces off one master, which
          // made the ungrouped form unreadable.
          const bySource = new Map<string, string[]>();
          for (const c of freeCrops) {
            bySource.set(c.from, [...(bySource.get(c.from) || []), c.display]);
          }
          const cropNote = bySource.size
            ? ` — ${[...bySource.entries()]
                .map(([from, aspects]) => `${aspects.join(', ')} cropped free from ${from}`)
                .join('; ')}`
            : '';
          const videoSizes = deliveredAspects.length
            ? `${deliveredAspects.join(' · ')}${cropNote}`
            : 'No video sizes live';

          return (
            <Box key={section.id}>
              <HStack spacing={2} mb={1.5} align="baseline">
                <Text fontSize="10px" fontWeight="800" textTransform="uppercase" letterSpacing="0.06em" color="brand.ink">
                  {section.label}
                </Text>
                <Text fontSize="10px" color="brand.muted">{section.note}</Text>
              </HStack>
              <Flex gap={2} wrap="wrap">
                <PresetButton
                  label="All static"
                  sizes={staticSizes.length ? staticSizes.join(' · ') : 'No static sizes live'}
                  count={section.staticFormatKeys.length}
                  countNoun="size"
                  on={section.staticOn}
                  disabled={!section.staticFormatKeys.length}
                  onToggle={() => onSelectionChange({
                    staticKeys: toggleKeySubset(staticKeys, section.staticFormatKeys, !section.staticOn),
                    videoKeys
                  })}
                />
                <PresetButton
                  label="All video"
                  sizes={videoSizes}
                  count={section.videoBillable}
                  countNoun="master"
                  on={section.videoOn}
                  disabled={!section.videoFormatKeys.length}
                  onToggle={() => onSelectionChange({
                    staticKeys,
                    videoKeys: toggleKeySubset(videoKeys, section.videoFormatKeys, !section.videoOn)
                  })}
                />
              </Flex>
            </Box>
          );
        })}
      </VStack>

      {/* Live spend shape. This is the guard rail on a multi-select picker:
          ticking sizes is the one operator action here that multiplies the
          bill, so the count is always on screen rather than discovered after
          the fact. */}
      <Text fontSize="11px" color="brand.muted" mt={3} data-testid="format-picker-spend" aria-live="polite">
        {staticKeys.length === 0 && !anyVideoOn ? (
          <>Pick at least one size. Nothing is selected, so Generate has nothing to render.</>
        ) : (
          <>
            <strong>
              {staticKeys.length > 0
                ? `${staticKeys.length} image generation${staticKeys.length === 1 ? '' : 's'} per concept`
                : 'No static ads'}
            </strong>
            {anyVideoOn && (
              <> · {renderVideoSpend(videoQuote)}</>
            )}
            {staticKeys.length > 0 && (
              <> — each size is typeset for its own safe area, so they can&apos;t be crops of one plate.</>
            )}
            {/* Said per platform, and only when true. The previous single
                sentence claimed the free PMax square on every video
                selection — including a 16:9-only run, where the backend
                does not mint it (the crop needs the 9:16 master), and
                including Meta-only runs, which have no free crop at all. */}
            {metaVideoTicked && (
              <> Meta bills <strong>one</strong> 9:16 master per product and derives its 1:1,
                 4:5 and Reels surfaces from that same master, free.</>
            )}
            {pmaxVideoTicked && (
              sharingConfirmed ? (
                // Mixed run, PMax's 9:16 requested, AND the preview quote
                // confirms sharing actually happened: PR #218 shares Meta's
                // already-billed 9:16 plate instead of minting PMax its own,
                // so this is the one PMax sentence that is NOT "9:16 and
                // 16:9 separately" — that statement is only true PMax-alone.
                <> PMax&apos;s 9:16 rides the same shared master Meta already bills for this
                   product{pmaxLandscapeTicked
                     ? <> — only its 16:9 is still a separate PMax master, since a landscape
                            can&apos;t be cropped out of a vertical.</>
                     : <>, so PMax adds no separate master for this selection.</>}
                   {' '}Its 1:1 still comes free from the 9:16.</>
              ) : (
                <> PMax bills each of 9:16 and 16:9 separately — a landscape can&apos;t be cropped
                   out of a vertical.{pmaxSquareFree
                     ? <> Its 1:1 comes free from the 9:16.</>
                     : <> Its free 1:1 needs the 9:16 selected.</>}</>
              )
            )}
          </>
        )}
      </Text>

      {/* ── Advanced: individual sizes ──────────────────────────────────
          One-off surface selection lives behind the accordion because it is
          the exception: the four buttons above cover the normal buy, and a
          grid of every size shown by default made the common case look as
          complicated as the rare one. Grouped by platform for the same reason
          the buttons are. */}
      <Accordion allowToggle mt={1}>
        <AccordionItem border="none">
          <AccordionButton px={0} py={2} _hover={{ bg: 'transparent' }} _expanded={{ color: 'rsViolet.700' }}>
            <Box flex="1" textAlign="left">
              <Text fontSize="11px" fontWeight="700" textTransform="uppercase" letterSpacing="0.06em" color="brand.muted">
                Advanced — choose individual sizes
              </Text>
            </Box>
            <AccordionIcon color="brand.muted" />
          </AccordionButton>
          <AccordionPanel px={0} pb={1} pt={1}>
            <VStack align="stretch" spacing={3}>
              {sections.map(section => (
                <Box key={section.id}>
                  <Text fontSize="10px" fontWeight="800" textTransform="uppercase" letterSpacing="0.06em" color="brand.muted" mb={1.5}>
                    {section.label}
                  </Text>
                  <Flex gap={2} wrap="wrap">
                    {section.cards.map(entry => (
                      <FormatCard
                        key={entry.key}
                        entry={entry}
                        formats={formats}
                        staticSet={staticSet}
                        videoSet={videoSet}
                        onToggleStatic={toggleStatic}
                        onToggleVideo={toggleVideo}
                      />
                    ))}
                  </Flex>
                </Box>
              ))}
            </VStack>
          </AccordionPanel>
        </AccordionItem>
      </Accordion>

      {fromFallback && (
        <Text fontSize="11px" color="brand.muted" mt={3}>
          Couldn&apos;t load the live format list — showing defaults. Formats
          marked coming soon stay unavailable until the catalog is reachable.
        </Text>
      )}
    </Box>
  );
}

// One surface tile in the Advanced grid. Extracted verbatim from the previous
// inline map so the accordion regrouping stays a move, not a rewrite — the
// derive-only handling below is the subtle part and is unchanged.
function FormatCard({
  entry, formats, staticSet, videoSet, onToggleStatic, onToggleVideo
}: {
  entry:          FormatEntry;
  formats:        FormatEntry[];
  staticSet:      Set<string>;
  videoSet:       Set<string>;
  onToggleStatic: (key: string) => void;
  onToggleVideo:  (key: string) => void;
}) {
  const id = entry.key as PlatformFormat;
  const isComingSoon = !isFormatLive(entry);
  const canImage = imageVideoKinds(entry).includes('image');
  // A derive-only surface declares kinds:['video'] but is produced by
  // cropping another master, so it is not independently generatable.
  const canVideo = imageVideoKinds(entry).includes('video')
    && !VIDEO_DERIVE_ONLY.includes(entry.key);
  const isDerivedVideo = imageVideoKinds(entry).includes('video')
    && VIDEO_DERIVE_ONLY.includes(entry.key);
  // The master this derive-only surface is a free crop of (only
  // pmax_video_1_1 has one today — see VIDEO_DERIVE_MASTER above).
  const derivedMasterKey = VIDEO_DERIVE_MASTER[entry.key];
  const derivedMasterEntry = derivedMasterKey ? formats.find(f => f.key === derivedMasterKey) : undefined;
  const derivedMasterOn = !isComingSoon && !!derivedMasterKey && videoSet.has(derivedMasterKey);
  // The shape this surface is cropped FROM, named on the badge so the
  // operator can see which other tick produces it. Falls back to a neutral
  // word rather than rendering "undefined" if the master is missing from a
  // partial catalog.
  const derivedMasterAspect = derivedMasterEntry?.aspectRatio || 'its master';
  const allowedKinds = imageVideoKinds(entry).filter(
    k => k === 'image' ? canImage : canVideo
  );
  const imageOn = !isComingSoon && staticSet.has(id);
  // A derive-only tile has no video tick of its own to read — the only
  // thing that determines whether the backend actually mints it is
  // whether its MASTER is selected, so mirror that here rather than
  // reading videoSet.has(id) (which would always be false and make the
  // tile look permanently unselected even when its master is on).
  const videoOn = !isComingSoon && (isDerivedVideo ? derivedMasterOn : videoSet.has(id));
  // A card is lit when it contributes ANY work, image or video.
  const isActive = imageOn || videoOn;

  return (
    <Box
      flex="1 1 calc(33% - 8px)"
      minW="180px"
      borderWidth="1px"
      borderColor={isActive ? 'rsViolet.400' : 'brand.border'}
      bg={isComingSoon ? 'brand.surface' : (isActive ? 'rsViolet.50' : 'white')}
      borderRadius="md"
      p={3}
      opacity={isComingSoon ? 0.55 : 1}
      cursor={isComingSoon ? 'not-allowed' : 'pointer'}
      onClick={() => {
        // coming_soon is display-only — never write it into selections.
        if (isComingSoon) return;
        // Body click toggles the card's STATIC contribution, which is
        // the multi-selectable one. A video-only surface (Reels) has no
        // static side, so it toggles video instead. Explicit control
        // over both stays on the chips below.
        if (canImage) onToggleStatic(id);
        else if (canVideo) onToggleVideo(id);
        // Derive-only (the free square): it is never its own submit, so
        // "selecting" it can only ever mean one thing — select the
        // master that makes it free.
        else if (isDerivedVideo && derivedMasterKey) onToggleVideo(derivedMasterKey);
      }}
      transition="border-color 0.12s, background 0.12s, opacity 0.12s"
      aria-disabled={isComingSoon || undefined}
    >
      <HStack spacing={2} flexWrap="wrap">
        <Text
          fontSize="sm"
          fontWeight="800"
          color={isComingSoon ? 'brand.muted' : (isActive ? 'rsViolet.700' : 'brand.ink')}
        >
          {entry.label}
        </Text>
        <Badge fontSize="9px" colorScheme="purple" variant="subtle">{entry.aspectRatio}</Badge>
        {isComingSoon && (
          <Badge fontSize="9px" colorScheme="gray" variant="subtle">Coming soon</Badge>
        )}
        {/* A crop always NAMES the size it comes from. "Included free" on its
            own left the operator to work out which other tick produces it,
            and the answer is not guessable — the square is cropped from the
            9:16 master specifically, not from whichever PMax video happens to
            be selected, so ticking 16:9 alone produces no square at all.
            Both states carry the source for that reason.

            Honesty fix, same badge: this used to read "Included free"
            unconditionally whenever the surface was live, promising a square
            that never renders unless its master is also selected. */}
        {isDerivedVideo && !isComingSoon && (
          derivedMasterOn ? (
            <Badge fontSize="9px" colorScheme="green" variant="subtle">
              Free crop of {derivedMasterAspect}
            </Badge>
          ) : (
            <Badge fontSize="9px" colorScheme="orange" variant="subtle">
              Needs {derivedMasterAspect} selected
            </Badge>
          )
        )}
      </HStack>
      <Text fontSize="11px" color="brand.muted" mt={1} mb={2}>{formatSubtitle(entry)}</Text>
      <HStack spacing={1}>
        {allowedKinds.map(k => {
          const selected = k === 'image' ? imageOn : videoOn;
          // Every chip on a live card is independently clickable — that IS
          // the multi-select. Only coming_soon freezes them.
          const disabled = isComingSoon;
          return (
            <Box
              key={k}
              as="button"
              type="button"
              onClick={(e: MouseEvent<HTMLButtonElement>) => {
                e.stopPropagation();
                if (disabled) return;
                if (k === 'image') onToggleStatic(id);
                else onToggleVideo(id);
              }}
              px={2}
              py={0.5}
              fontSize="10px"
              fontWeight="700"
              textTransform="capitalize"
              borderWidth="1px"
              borderColor={selected && !isComingSoon ? 'rsViolet.500' : 'brand.border'}
              bg={selected && !isComingSoon ? 'rsViolet.500' : 'white'}
              color={selected && !isComingSoon ? 'white' : 'brand.muted'}
              borderRadius="sm"
              opacity={disabled ? 0.45 : 1}
              cursor={disabled ? 'default' : 'pointer'}
              disabled={isComingSoon || undefined}
            >
              {k}
            </Box>
          );
        })}
        {/* Derive-only surfaces have no chip of their own in allowedKinds
            (canVideo is false for them), so without this the tile would
            show zero controls. Clicking it toggles the MASTER, same as
            the card body. */}
        {isDerivedVideo && !isComingSoon && (
          <Box
            as="button"
            type="button"
            onClick={(e: MouseEvent<HTMLButtonElement>) => {
              e.stopPropagation();
              if (derivedMasterKey) onToggleVideo(derivedMasterKey);
            }}
            px={2}
            py={0.5}
            fontSize="10px"
            fontWeight="700"
            textTransform="capitalize"
            borderWidth="1px"
            borderColor={derivedMasterOn ? 'rsViolet.500' : 'brand.border'}
            bg={derivedMasterOn ? 'rsViolet.500' : 'white'}
            color={derivedMasterOn ? 'white' : 'brand.muted'}
            borderRadius="sm"
            cursor="pointer"
          >
            video
          </Box>
        )}
      </HStack>
    </Box>
  );
}


// platformFormat + adKinds are DERIVED from the ticked surfaces rather than
// picked directly. Keeping them on the selections object means Step 2 (which
// media rails to show) and the brief editor / preview (which need one format for
// context) carry on reading exactly what they read before this became a
// multi-select — so the checkbox rewrite stays inside the picker.
//
// platformFormat is the FIRST ticked surface, static before video: it is the
// run-level context format, and the backend stamps each Ad row with its own
// surface anyway (per-row `platformFormat: fmt`), so this never mislabels the
// other sizes.
// Leave scene-reel mode: adKinds / platformFormat re-derive from the ticks that
// were kept the whole time, so the standard selection comes back exactly.
function toStandardWorkflow(sel: WizardSelections): WizardSelections {
  return {
    ...sel,
    workflow: 'standard',
    ...deriveFormatFields(sel.staticFormatKeys, sel.videoFormatKeys)
  };
}

export function deriveFormatFields(staticKeys: string[], videoKeys: string[]): {
  platformFormat: PlatformFormat;
  adKinds:        AdKindsSelection;
} {
  const adKinds: AdKindsSelection =
    staticKeys.length && videoKeys.length ? 'both'
    : videoKeys.length                    ? 'video'
    : 'image';
  return {
    adKinds,
    platformFormat: (staticKeys[0] || videoKeys[0] || 'meta_feed_1_1') as PlatformFormat
  };
}

export type WizardStepKey = 'campaign' | 'products' | 'generate';

// Default template fanout — all five ai_* templates run per seed so
// the LLM has the widest creative-style menu without operator picking.
// Variety within a template comes from the four Director concepts.
const DEFAULT_TEMPLATE_IDS = [
  'ai_brand_led',
  'ai_ugc_led',
  'ai_social_proof_led',
  'ai_editorial',
  'ai_promotional'
];

export type PlatformFormat =
  | 'meta_feed_1_1'
  | 'meta_feed_4_5'
  | 'meta_reels_9_16'
  | 'meta_stories_9_16'
  | 'pmax_16_9';

export type AdKindsSelection = 'image' | 'video' | 'both';

// 'standard' is the platform-format picker flow. 'scene_reel' is the whole-run
// scene reel workflow (API contract §2) — only offered when GET
// /api/ads/formats lists it as enabled, never for a brand-kind campaign.
export type WizardWorkflow = 'standard' | 'scene_reel';

// Step 2 reads platformFormat + adKinds to decide which rails to show. A scene
// reel is video-only at 9:16 first, so this surface lights the video seed rail
// (relabelled "preferred scene images") and hides the image-ad queue. It is
// never sent: the scene-reel request body has no platformFormat / kinds.
const SCENE_REEL_RAIL_FORMAT: PlatformFormat = 'meta_stories_9_16';

// Display-only snapshot for Step 4's review summary. Populated by
// Step 2 from the product/media rows + campaign fetch it already
// loaded — never POSTed to /api/ads/generate. Deep-link into
// ?step=generate may leave this empty; Step 4 falls back to a
// best-effort hydrate (campaign name + media by ids; products
// degrade to a count because there is no products-by-ids endpoint
// usable without the catalog page).
export type SeedPick = { productId: string; mediaId: string };

export type SelectionSummary = {
  campaignName?: string;
  products: Array<{ id: string; title: string; imageUrl: string | null }>;
  media:    Array<{ id: string; thumbUrl: string }>;
};

export type WizardSelections = {
  campaignId:   string | null;
  // Hydrated by Step 2 from /api/campaigns/:id. Lets the wizard know
  // when a brand-mode campaign is active so we can relax Step 2's
  // "must pick something" gate (brand campaigns seed brand_match
  // media on the backend without explicit picks).
  campaignKind: string | null;
  // Display-only; see SelectionSummary. Not part of the generate body.
  selectionSummary: SelectionSummary;
  // Platform-format-aware ad generation (Phase 2 wizard picker).
  // Drives the Director + HTML Gen format constraints + safe-area
  // pixel boxes downstream. Default 'meta_feed_1_1' preserves legacy
  // behavior (no extra constraints — same as Director was producing
  // pre-Phase-2). Sent through /api/ads/generate body to expand-
  // WizardJob, which overrides Campaign.platformFormat for this run.
  // DERIVED from staticFormatKeys / videoFormatKeys — see syncFormatSelection.
  // Kept on the selections object because Step 2 reads both to decide which
  // media rails to show, and the brief editor / preview need a single format for
  // context. They are no longer what the picker writes.
  platformFormat: PlatformFormat;
  // image / video / both per run. Backend's resolveKinds() (services/
  // platformFormats.js) intersects this with the selected format's
  // declared kinds (Reels is video-only), so 'image' on Reels gets
  // clamped server-side. DERIVED — see platformFormat above.
  adKinds: AdKindsSelection;
  // THE PICKER'S SOURCE OF TRUTH — the surfaces the operator ticked. Sent as
  // staticFormats / videoFormats with preset:'explicit'.
  //
  // MONEY: every staticFormatKeys entry is its own billable image generation
  // per concept (the image model typesets copy into the pixels, so a size
  // cannot be a crop of another size). videoFormatKeys holds AT MOST ONE entry
  // — one video per product is what the pipeline makes, and the backend clamps
  // it regardless of what we send.
  staticFormatKeys: string[];
  videoFormatKeys:  string[];
  // Legacy three-knob flag. Retained so a stale caller / restored state stays
  // valid, but the picker no longer sets it: with an explicit list of ticked
  // surfaces there is nothing left to "expand". Always sent false.
  expandStaticFormats: boolean;
  // Target video length in seconds. Only meaningful when adKinds
  // includes video; Step 4 omits it from the generate body otherwise.
  // Default 10s — Google Omni's standard slot; other lengths snap to
  // the nearest supported Omni duration server-side.
  videoDurationSec: number;
  productIds:  string[];
  // Pre-populated when the wizard is deep-linked from the media
  // library's "Generate Ads" button. Each id rides through to the
  // POST /api/ads/generate payload as mediaIds[]; the campaign service
  // dispatches by ProductMatchArtifact.outcome to determine product
  // featuring.
  mediaIds:    string[];
  templateIds: string[];
  ctaText:     string;
  ctaUrl:      string;
  urlParams:   string;       // raw querystring fragment, e.g. utm_source=...&utm_medium=...
  // Pairing-level exclusions surfaced from Step 2's match ribbons.
  // Each entry is `${productId||'null'}|${mediaId}` — POST'd to
  // /api/ads/generate as [{productId, mediaId}] objects so the
  // backend can drop the matching tuples from the cartesian.
  // brand_match exclusions use productId='null' (no specific SKU).
  excludedPairings: string[];
  // Product-kind tier toggles. Default false — product campaigns
  // only seed strict product_match UGC unless the operator clicks
  // the "Include category-matched" / "Include brand-matched" expand
  // buttons in Step 2. Plumbed through Step 4 into the
  // POST /api/ads/generate body where seedsFromProduct consumes them.
  includeCategoryMatched: boolean;
  includeBrandMatched:    boolean;
  // Deterministic-video controls (Phase 4). All optional on the
  // backend — empty/false = default deterministic product video.
  // directorVariants: additive AI-directed concepts on top of the
  // standard product video. Hidden for brand-kind campaigns (those
  // are always director-driven; no product to be deterministic about).
  directorVariants: boolean;
  // Extra creative direction merged into the canonical prompt
  // (mechanics preserved). ≤1000 chars. Ignored when videoPromptRaw
  // is set (raw replaces the whole prompt).
  videoPromptGuidance: string;
  // Full raw prompt replacement for model-generated videos. ≤4000.
  // Bypasses product-fidelity + no-text safeguards.
  videoPromptRaw: string;
  // ORDERED catalog-product Media ids for the video reference stack
  // (position 0 = primary seed). Distinct from mediaIds (UGC posts).
  // Selection order is load-bearing — never a Set. Empty ⇒ backend
  // defaults to feed-order hero + first two alts.
  // ORDERED (productId, mediaId) pairs — the video reference stack per product.
  // A PAIR rather than a bare mediaId because one post can legitimately seed two
  // products' videos, and because the backend must not have to guess which
  // product a related/social pick belongs to (that guess could flip when detect
  // rewrote its match scores, changing the ad's identityDigest).
  seedPicks: SeedPick[];
  // Scene reel (API contract §2). `workflow` picks the request builder in
  // Step 4: 'scene_reel' posts sceneReelRequest.ts's body and ignores every
  // format/kind/duration/prompt field above. The ticked surfaces stay as they
  // were, so switching back to 'standard' restores the exact selection.
  workflow: WizardWorkflow;
  // Clip model key from the workflow catalog (models[].key). null until the
  // scene reel is chosen; the backend resolves an absent model to its default.
  sceneModel: string | null;
  // Preferred scene images — ≤ maxSeedPicksPerProduct per product, in scene
  // order; empty lets the reel choose. Deliberately NOT seedPicks: Step 2
  // pre-fills seedPicks with the video reference stack (primary + first alts),
  // which a scene reel would read as deliberate lifestyle picks.
  sceneSeedPicks: SeedPick[];
  // Plan-studio handoff. Omitted from a normal visit. capUsd is shown
  // on the review step and, with the plan line, posted as capUsd.
  // The server reads planRevision, not revision. creativeType and
  // generateCount are posted when the suggestion carried them.
  capUsd?: number | null;
  planId?: string | null;
  planLineKey?: string | null;
  planRevision?: string | null;
  creativeType?: string | null;
  generateCount?: number | null;
  lineCampaignRequired?: boolean;
};

const STEPS: Array<{ key: WizardStepKey; label: string; description: string }> = [
  { key: 'campaign', label: 'Campaign', description: 'Pick a synced campaign or create one' },
  { key: 'products', label: 'Products', description: 'Select what to feature' },
  { key: 'generate', label: 'Generate', description: 'Review & render' }
];

// Brand-kind campaigns frame Step 2 as a media-first selection (one
// unified ribbon mixing catalog imagery + UGC). Override the visible
// label + description without changing the URL slug 'products' so
// back/forward + deep links stay stable.
function stepsForKind(kind: string | null): typeof STEPS {
  if (kind !== 'brand') return STEPS;
  return STEPS.map(s => s.key === 'products'
    ? { ...s, label: 'Media', description: 'Select Media — catalog imagery and social posts' }
    : s);
}

function stepIndex(k: WizardStepKey): number {
  return STEPS.findIndex(s => s.key === k);
}

export function GenerateAdsWizard() {
  const [params, setParams] = useSearchParams();
  const initialCampaignId = params.get('campaignId');
  // ?mediaIds=X,Y,Z — comma-separated list deep-linked from the media
  // library's "Generate Ads" button. ?productIds=A,B can also pre-pop
  // from a future product-catalog deep-link.
  const initialMediaIds = (params.get('mediaIds') || '')
    .split(',').map(s => s.trim()).filter(Boolean);
  const initialProductIds = (params.get('productIds') || '')
    .split(',').map(s => s.trim()).filter(Boolean);
  const handoff = readGenerateHandoff(params);

  // Whether this wizard session began from a specific product's "Generate
  // Ads" button. Captured at mount because the cleanup effect strips
  // ?productIds from the URL once absorbed, so the param cannot be re-read
  // later. Step 2 uses it to leave the campaign's pinned items alone.
  const deepLinkedToProductRef = useRef(initialProductIds.length > 0);
  const deepLinkedToProduct = deepLinkedToProductRef.current;

  const stepFromUrl = (params.get('step') as WizardStepKey) || 'campaign';
  const step: WizardStepKey = STEPS.some(s => s.key === stepFromUrl) ? stepFromUrl : 'campaign';

  const [selections, setSelections] = useState<WizardSelections>({
    campaignId:   initialCampaignId,
    campaignKind: null,
    productIds:   initialProductIds,
    mediaIds:     initialMediaIds,
    // Settings step was dropped — defaults below replace what the
    // operator used to author. Templates run all 5 ai_* by default,
    // CTA text/URL/UTMs ship blank and the backend fills them in
    // (copy_candidates for cta_text, brand.websiteUrl for ctaUrl,
    // no UTM tracking).
    templateIds:  DEFAULT_TEMPLATE_IDS.slice(),
    ctaText:      '',
    ctaUrl:       '',
    urlParams:    '',
    // NOTHING IS PRE-TICKED. You are only ever billed for what you clicked.
    //
    // This used to open on Meta 1:1 for BOTH kinds, reproducing the old
    // resolvePreset('single') default. Combined with preset buttons that ADD to
    // the selection rather than replace it, that made every preset click carry a
    // stowaway: "PMax All video" on a fresh wizard resolved server-side to
    //   staticFormats: ['meta_feed_1_1']            -> 3 unordered image gens
    //   videoFormats:  ['meta_stories_9_16', ...]   -> a THIRD unordered master,
    // because resolveExplicitFormats promotes any Meta video tick to the Meta
    // master. Ordered 2 masters (~$1.80); billed 3 masters + 3 statics (~$2.91).
    //
    // An empty start is the only shape that cannot do that, because there is no
    // leftover to inherit. It costs one extra click and is already a supported
    // state: Step4Generate gates the button on `noFormatSelected`, mirroring the
    // backend's NO_GENERATABLE_FORMAT refusal.
    staticFormatKeys: [],
    videoFormatKeys:  [],
    platformFormat: 'meta_feed_1_1',
    expandStaticFormats: false,
    adKinds:        handoff.kinds ?? 'image',
    videoDurationSec: 10,
    excludedPairings: [],
    includeCategoryMatched: false,
    includeBrandMatched:    false,
    directorVariants:       false,
    videoPromptGuidance:    '',
    videoPromptRaw:         '',
    seedPicks:              [],
    workflow:               'standard',
    sceneModel:             null,
    sceneSeedPicks:         [],
    selectionSummary:       { products: [], media: [] },
    capUsd:                 handoff.capUsd,
    planId:                 handoff.planId,
    planLineKey:            handoff.planLineKey,
    planRevision:           handoff.revision,
    creativeType:           handoff.creativeType,
    generateCount:          handoff.generateCount,
    lineCampaignRequired:   handoff.lineCampaignRequired
  });

  // Live format catalog (GET /api/ads/formats). Seeded with a conservative
  // fallback so the picker never blanks out while the request is in flight.
  const formatCatalog = useFormatCatalog();

  // Prune any ticked surface the live catalog won't generate — a coming_soon or
  // unknown key from a stale deep link, restored wizard state, or a format the
  // catalog dropped. Without this the operator can hit Generate and eat a 400
  // (PLATFORM_FORMAT_COMING_SOON, or NO_GENERATABLE_FORMAT once every tick is
  // stale). Also drops a surface ticked for a kind it cannot carry, which is the
  // same clamp the backend applies — better to show the honest selection than to
  // silently generate less than the picker displays.
  //
  // If pruning empties the selection entirely, fall back to the first live
  // surface as a static tick so the wizard is never in a state where Generate
  // cannot work at all.
  useEffect(() => {
    if (formatCatalog.loading) return;
    const liveFor = (key: string, kind: 'image' | 'video') => {
      const entry = formatCatalog.byKey.get(key);
      return isFormatLive(entry) && imageVideoKinds(entry).includes(kind);
    };
    setSelections(prev => {
      const nextStatic = prev.staticFormatKeys.filter(k => liveFor(k, 'image'));
      const nextVideo  = prev.videoFormatKeys.filter(k => liveFor(k, 'video'));
      const changed =
        nextStatic.length !== prev.staticFormatKeys.length ||
        nextVideo.length  !== prev.videoFormatKeys.length;
      if (!changed) return prev;
      // In scene-reel mode adKinds / platformFormat belong to the scene reel
      // (see SCENE_REEL_RAIL_FORMAT); only the hidden ticks are pruned.
      if (prev.workflow === 'scene_reel') {
        return { ...prev, staticFormatKeys: nextStatic, videoFormatKeys: nextVideo };
      }
      // NO SUBSTITUTE TICK. Pruning used to fall back to the first live surface
      // as a static tick "so Generate always works" — but that invents a
      // billable image the operator never chose, which is the same stowaway
      // class as the old mount default. An empty selection is honest and
      // already handled: Step4Generate disables the button on
      // `noFormatSelected` and the picker prints "pick at least one size".
      return { ...prev, staticFormatKeys: nextStatic, videoFormatKeys: nextVideo,
               ...deriveFormatFields(nextStatic, nextVideo) };
    });
  }, [formatCatalog.loading, formatCatalog.formats, formatCatalog.byKey]);

  // The scene-reel workflow this brand may use, or null. GET /api/ads/formats
  // is brand-agnostic; whether THIS brand is on the allowlist is ad-readiness
  // `sceneReel.allowed` (API contract §2a, owner decision 6). Until that answer
  // lands — or when it is absent or false — the workflow is not offered at all,
  // so an off-list brand never sees it. The API refuses it regardless (403).
  const readiness = useAdReadiness();
  const sceneReelWorkflow = formatCatalog.sceneReelWorkflow && sceneReelBrandAllowed(readiness.sceneReel)
    ? formatCatalog.sceneReelWorkflow
    : null;

  // A scene-reel selection needs a live workflow for this brand. If there is
  // none (flag off, brand not on the allowlist, active brand switched, older
  // backend, catalog outage) fall back to standard ads rather than leave the
  // operator on a workflow nobody can run.
  useEffect(() => {
    if (formatCatalog.loading || sceneReelWorkflow) return;
    setSelections(prev => prev.workflow === 'scene_reel' ? toStandardWorkflow(prev) : prev);
  }, [formatCatalog.loading, sceneReelWorkflow]);

  // Clean up one-shot deep-link params (mediaIds / productIds) once
  // we've absorbed them into state — those were carried in from the
  // Media Library / Catalog Browser "Generate Ads" buttons and shouldn't
  // re-trigger on back/forward. campaignId DOES stay in the URL —
  // it's the wizard's session anchor, so refreshing on any step keeps
  // the campaign context (and lets Step 2 hydrate the campaignKind
  // before rendering the picker, instead of falling through to the
  // legacy "other kinds" layout on a kindless re-init).
  useEffect(() => {
    if (initialMediaIds.length || initialProductIds.length) {
      const next = new URLSearchParams(params);
      next.delete('mediaIds');
      next.delete('productIds');
      next.set('step', step);
      setParams(next, { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const goTo = (k: WizardStepKey) => {
    const next = new URLSearchParams(params);
    next.set('step', k);
    // Keep campaignId in the URL on every step navigation so a refresh
    // on Step 2/3/4 re-hydrates the wizard with the right campaign
    // context (and the right Step 2 layout via campaignKind).
    if (selections.campaignId) next.set('campaignId', selections.campaignId);
    setParams(next, { replace: false });
  };
  const back = () => {
    const i = stepIndex(step);
    if (i > 0) goTo(STEPS[i - 1].key);
  };
  const next = () => {
    const i = stepIndex(step);
    if (i < STEPS.length - 1) goTo(STEPS[i + 1].key);
  };

  const update = (patch: Partial<WizardSelections>) =>
    setSelections(prev => {
      // Changing the campaign invalidates downstream selections — but
      // ONLY when the operator is switching between two real campaigns
      // mid-wizard. The first-time selection (prev.campaignId is null →
      // first pick) must preserve any deep-linked productIds / mediaIds
      // the wizard arrived with from the Media Library or Catalog
      // Browser, otherwise they're silently dropped on Step 1.
      const switchingExistingCampaign =
        patch.campaignId !== undefined
        && patch.campaignId !== prev.campaignId
        && !!prev.campaignId;
      if (switchingExistingCampaign) {
        return {
          ...prev,
          ...patch,
          productIds: [],
          mediaIds: [],
          excludedPairings: [],
          includeCategoryMatched: false,
          includeBrandMatched: false,
          seedPicks: [],
          sceneSeedPicks: [],
          directorVariants: false,
          videoPromptGuidance: '',
          videoPromptRaw: ''
        };
      }
      const merged = { ...prev, ...patch };
      // Brand campaigns have no product to build a scene reel around.
      return merged.workflow === 'scene_reel' && merged.campaignKind === 'brand'
        ? toStandardWorkflow(merged)
        : merged;
    });

  // Offered only when the backend lists the workflow, this brand is on its
  // allowlist (sceneReelWorkflow above) AND the campaign is not brand-kind.
  // While campaignKind is still unknown (Step 1) it is offered; the update()
  // guard above takes it back if the campaign turns out brand.
  const showWorkflowPicker = !!sceneReelWorkflow && selections.campaignKind !== 'brand';
  const sceneReelMode = selections.workflow === 'scene_reel' && !!sceneReelWorkflow;
  const setWorkflow = (next: WizardWorkflow) =>
    setSelections(prev => {
      if (next === prev.workflow) return prev;
      if (next === 'standard') return toStandardWorkflow(prev);
      return {
        ...prev,
        workflow:       'scene_reel',
        sceneModel:     prev.sceneModel ?? defaultSceneModelKey(sceneReelWorkflow),
        adKinds:        'video',
        platformFormat: SCENE_REEL_RAIL_FORMAT
      };
    });

  // Step → can-proceed gate. Each step decides what it requires; the
  // Kind-aware step labels for the Stepper + Next button. The
  // underlying STEPS keys / URL slugs stay stable.
  const displayedSteps = useMemo(() => stepsForKind(selections.campaignKind), [selections.campaignKind]);

  // wizard footer reads this to enable/disable Next.
  const canProceed = useMemo(() => {
    if (step === 'campaign') return !!selections.campaignId;
    // Brand-mode campaigns can proceed with no picks — the seeder
    // falls back to brand_match media. Product/synced campaigns
    // still require a picked product or media.
    if (step === 'products') {
      return selections.productIds.length > 0
          || selections.mediaIds.length > 0
          || selections.campaignKind === 'brand';
    }
    return true;
  }, [step, selections]);

  const showVideoDuration = selections.adKinds === 'video' || selections.adKinds === 'both';

  // Is any of the four platform quick-select presets fully active right now?
  // Drives the express "Quick generate" control below. Uses the SAME
  // liveFormatKeys / isPresetFullySelected helpers PlatformFormatPicker uses
  // internally, so "the button reads as on" and "express mode is available"
  // can never disagree.
  const quickPresetActive = useMemo(() => {
    const formats   = formatCatalog.formats;
    const staticSet = new Set(selections.staticFormatKeys);
    const videoSet  = new Set(selections.videoFormatKeys);
    return isPresetFullySelected(liveFormatKeys(formats, 'image', isMetaFormat), staticSet)
        || isPresetFullySelected(liveFormatKeys(formats, 'video', isMetaFormat), videoSet)
        || isPresetFullySelected(liveFormatKeys(formats, 'image', isPmaxFormat), staticSet)
        || isPresetFullySelected(liveFormatKeys(formats, 'video', isPmaxFormat), videoSet);
  }, [formatCatalog.formats, selections.staticFormatKeys, selections.videoFormatKeys]);

  return (
    <VStack align="stretch" spacing={6}>
      <PageHeader
        eyebrow="Generate Ads"
        title="New ad batch"
        description="Pick a campaign, choose products or media, and render. Templates and CTA copy are AI-driven — no setup required."
      />

      <Stepper current={step} steps={displayedSteps} />

      {/* Workflow choice — absent entirely unless the backend lists an
          enabled scene-reel workflow, so the standard wizard is unchanged. */}
      {showWorkflowPicker && sceneReelWorkflow && (
        <WorkflowPicker
          value={sceneReelMode ? 'scene_reel' : 'standard'}
          workflow={sceneReelWorkflow}
          onChange={setWorkflow}
        />
      )}

      {/* Platform format picker — wizard-level, persistent across every
          step. Lives outside the per-step Box so it's visible even when
          operators enter via deep-links that skip Step 1 (Campaigns
          list "Generate" button, NewCampaignModal, CampaignDetail —
          all jump to ?step=products with the campaign pre-selected).
          A scene reel has a fixed output set, so it shows a read-only card
          instead; the picker's ticks are kept for "Standard ads". */}
      {sceneReelMode && sceneReelWorkflow ? (
        <SceneReelOutputsCard workflow={sceneReelWorkflow} />
      ) : (
        <PlatformFormatPicker
          staticKeys={selections.staticFormatKeys}
          videoKeys={selections.videoFormatKeys}
          onSelectionChange={({ staticKeys, videoKeys }) => update({
            staticFormatKeys: staticKeys,
            videoFormatKeys:  videoKeys,
            // Keep the derived fields in lockstep with the ticks in ONE place, so
            // Step 2's rails and the brief editor can never read a stale kind.
            ...deriveFormatFields(staticKeys, videoKeys)
          })}
          formats={formatCatalog.formats}
          fromFallback={formatCatalog.fromFallback}
          quoteContext={{
            campaignId:       selections.campaignId,
            productIds:       selections.productIds,
            templateIds:      selections.templateIds,
            videoDurationSec: selections.videoDurationSec
          }}
        />
      )}

      {/* TESTING ACCELERATOR ONLY (owner request, 2026-08-11) — visible on any
          step, once one of the four quick-select buttons above is active, so
          an operator never has to hunt for it. Skips Step 2's manual product/
          media picking and jumps straight to Generate using resolved
          defaults; hidden once already there since it would be a no-op. */}
      {quickPresetActive && step !== 'generate' && !sceneReelMode && (
        <ExpressGenerateBar
          selections={selections}
          onResolved={(patch) => { update(patch); goTo('generate'); }}
        />
      )}

      {sceneReelMode && sceneReelWorkflow ? (
        <SceneReelControls
          selections={selections}
          workflow={sceneReelWorkflow}
          onChange={update}
        />
      ) : showVideoDuration && (
        <VideoControlsStrip
          selections={selections}
          onChange={update}
        />
      )}

      <Box>
        {step === 'campaign' && <Step1Campaign value={selections} onChange={update} />}
        {step === 'products' && <Step2Picker value={selections} onChange={update} deepLinkedToProduct={deepLinkedToProduct} />}
        {step === 'generate' && (
          <Step4Generate value={selections} sceneReelWorkflow={sceneReelMode ? sceneReelWorkflow : null} />
        )}
      </Box>

      <HStack justify="space-between" pt={2}>
        <Button as={RouterLink} to="/campaigns" variant="ghost" size="sm">
          Cancel
        </Button>
        <HStack spacing={2}>
          {stepIndex(step) > 0 && (
            <Button onClick={back} variant="outline" size="sm">Back</Button>
          )}
          {step !== 'generate' ? (
            <Button onClick={next} variant="brand" size="sm" isDisabled={!canProceed}>
              Next: {displayedSteps[stepIndex(step) + 1]?.label}
            </Button>
          ) : null}
        </HStack>
      </HStack>
    </VStack>
  );
}

// How many products/media the express path defaults to when it has to
// resolve its own seed set (no campaign pins, no deep-linked picks). Small on
// purpose — this is a testing accelerator, not a bulk-run tool. Not sourced
// from any backend constant; picked to sit comfortably under the render
// pipeline's per-run cap (MAX_CREATIVES_PER_RUN = 6 — see Step2Picker.tsx)
// even before format/template fan-out is considered.
const EXPRESS_DEFAULT_SEED_COUNT = 4;

// Express / quick-generate — TESTING ACCELERATOR ONLY (owner request,
// 2026-08-11). Resolves a real default seed set and jumps straight to
// Step 4 (Generate), skipping Step 2's manual product/media picking so an
// operator can smoke-test a format combination without hand-picking
// anything. It does NOT relax Step 4's own "seedCount === 0" gate — the
// resolution below either finds a genuine default or refuses with a reason,
// so an empty/invalid run stays impossible from here same as everywhere else.
function ExpressGenerateBar({
  selections,
  onResolved
}: {
  selections: WizardSelections;
  onResolved: (patch: Partial<WizardSelections>) => void;
}) {
  const { activeBrand } = useBrand();
  const brandId = activeBrand?.id || null;
  const toast = useToast();
  const [busy, setBusy] = useState(false);

  const run = async () => {
    if (!selections.campaignId) {
      toast({ title: 'Pick a campaign first', status: 'warning', duration: 4000 });
      return;
    }
    // Already have something to generate from — a deep link (?productIds=…)
    // or a manual pick made before the operator turned a quick-select preset
    // on. Respect it rather than clobbering it with campaign defaults; just
    // advance.
    if (selections.productIds.length > 0 || selections.mediaIds.length > 0) {
      onResolved({});
      return;
    }
    setBusy(true);
    try {
      const campaignResp = await apiJson<{
        campaign:       { kind: string | null } | null;
        pinnedProducts: { id: string }[];
        pinnedMedia:    { id: string }[];
      }>(`/api/campaigns/${encodeURIComponent(selections.campaignId)}`);
      const campaignKind     = campaignResp.campaign?.kind || null;
      const pinnedProductIds = (campaignResp.pinnedProducts || []).map(p => p.id);
      const pinnedMediaIds   = (campaignResp.pinnedMedia    || []).map(m => m.id);

      // Priority mirrors the normal Step 2 flow: campaign-pinned products
      // first (that IS "the campaign's existing product set"), then fall
      // back to the brand's own catalog, then pinned media as a last resort
      // for a campaign with no product pins at all.
      let productIds = pinnedProductIds.slice(0, EXPRESS_DEFAULT_SEED_COUNT);
      let mediaIds: string[] = [];

      if (!productIds.length && brandId) {
        const catalogResp = await apiJson<{ products: { id: string }[] }>(
          `/api/catalog?brandId=${encodeURIComponent(brandId)}&limit=${EXPRESS_DEFAULT_SEED_COUNT}`
        );
        productIds = (catalogResp.products || []).map(p => p.id).slice(0, EXPRESS_DEFAULT_SEED_COUNT);
      }

      if (!productIds.length && pinnedMediaIds.length) {
        mediaIds = pinnedMediaIds.slice(0, EXPRESS_DEFAULT_SEED_COUNT);
      }

      if (!productIds.length && !mediaIds.length) {
        // Refuse rather than advance to a Generate screen whose button can
        // never enable — Step 4's own gate requires productIds.length > 0 ||
        // mediaIds.length > 0 regardless of campaignKind, so silently
        // advancing here would just relocate the dead end.
        toast({
          title: 'No defaults to generate from',
          description: 'This campaign has no pinned products/media and the brand catalog is empty. Pick products or media on the Products step instead.',
          status: 'error',
          duration: 8000
        });
        return;
      }

      onResolved({ campaignKind, productIds, mediaIds });
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'unknown error';
      toast({ title: 'Could not resolve defaults', description: msg, status: 'error', duration: 6000 });
    } finally {
      setBusy(false);
    }
  };

  return (
    <Box borderWidth="1px" borderColor="orange.300" borderRadius="lg" bg="orange.50" p={3}>
      <HStack justify="space-between" align="center" wrap="wrap" spacing={3}>
        <Box>
          <Text fontSize="11px" fontWeight="800" textTransform="uppercase" letterSpacing="0.06em" color="orange.700">
            Testing accelerator
          </Text>
          <Text fontSize="xs" color="brand.ink" maxW="480px">
            A quick-select format preset is active. Skip picking products/media by hand and
            generate straight from campaign defaults.
          </Text>
        </Box>
        <Button
          size="sm"
          colorScheme="orange"
          variant="solid"
          onClick={run}
          isLoading={busy}
          loadingText="Resolving defaults…"
          isDisabled={!selections.campaignId}
        >
          Quick generate — use defaults
        </Button>
      </HStack>
    </Box>
  );
}

function Stepper({ current, steps }: { current: WizardStepKey; steps: typeof STEPS }) {
  const idx = stepIndex(current);
  return (
    <Flex
      borderWidth="1px"
      borderColor="brand.border"
      borderRadius="lg"
      bg="brand.surface"
      px={4}
      py={3}
      gap={3}
      align="center"
      wrap="wrap"
    >
      {steps.map((s, i) => {
        const status = i < idx ? 'done' : i === idx ? 'active' : 'pending';
        return (
          <HStack key={s.key} spacing={2} flex={1} minW="180px">
            <Box
              w="24px" h="24px"
              borderRadius="full"
              bg={status === 'active' ? 'rsViolet.500' : status === 'done' ? 'green.400' : 'gray.200'}
              color="white"
              display="flex" alignItems="center" justifyContent="center"
              fontSize="xs" fontWeight="800"
              flexShrink={0}
            >
              {status === 'done' ? '✓' : i + 1}
            </Box>
            <Box minW={0}>
              <Text fontSize="sm" fontWeight="700" color={status === 'active' ? 'rsViolet.700' : 'brand.ink'} noOfLines={1}>
                {s.label}
              </Text>
              <Text fontSize="10px" color="brand.muted" noOfLines={1}>{s.description}</Text>
            </Box>
            {status === 'active' && <Badge fontSize="9px" colorScheme="purple" variant="subtle">Now</Badge>}
          </HStack>
        );
      })}
    </Flex>
  );
}

export function StepShell({ heading, helper, children }: { heading: string; helper?: string; children: React.ReactNode }) {
  return (
    <Box borderWidth="1px" borderColor="brand.border" borderRadius="lg" bg="brand.surface" p={5}>
      <Heading size="sm" textTransform="uppercase" letterSpacing="0.04em" color="brand.ink" mb={1}>{heading}</Heading>
      {helper && <Text fontSize="xs" color="brand.muted" mb={4}>{helper}</Text>}
      {children}
    </Box>
  );
}

// Persistent video-controls strip — duration + director toggle + prompt
// guidance / Advanced raw editor. Only mounted when adKinds includes
// video (same gate as the old duration Select).
type ScaffoldResp = {
  prompt:       string;
  model:        string;
  aspectRatio:  string;
  durationSec:  number;
  byteCap:      number;
  // The scaffold prompt is an APPROXIMATION of what gets submitted, and the
  // endpoint now says so rather than leaving the operator to assume otherwise.
  //
  // It is NOT a different builder — the backend calls the same canonical
  // buildVeoPrompt. But it runs BEFORE an ad exists, so it hardcodes
  // seedHasText=false and hasProductReference=true and omits five inputs the
  // real render passes. Consequence worth knowing while editing this box: the
  // burned-in-text guard block (+283 bytes) can never appear here, and at a
  // 4096-byte cap its absence is why a `Product:` line can survive in this
  // preview and be dropped from the real submission — /^Product: / heads the
  // builder's DROP_PRIORITY list.
  //
  // Optional so the panel still renders against a backend that predates the
  // field (the backend PR must land first for this to populate).
  approximation?: {
    isApproximation: boolean;
    reason: string;
    assumedInputs?: { field: string; assumed: unknown; note: string }[];
    omittedInputs?: string[];
    exactPromptAvailableAt?: string;
  } | null;
};

function VideoControlsStrip({
  selections,
  onChange
}: {
  selections: WizardSelections;
  onChange:   (patch: Partial<WizardSelections>) => void;
}) {
  const toast = useToast();
  const [scaffoldLoading, setScaffoldLoading] = useState(false);
  const [scaffoldMeta, setScaffoldMeta] = useState<{
    model: string;
    byteCap: number;
    approximation?: ScaffoldResp['approximation'];
  } | null>(null);
  // Drop the stale "Model · byte cap" label when the campaign changes —
  // the strip isn't remounted, so its local scaffold meta would linger.
  useEffect(() => { setScaffoldMeta(null); }, [selections.campaignId]);
  const rawActive = selections.videoPromptRaw.trim().length > 0;
  // Brand campaigns are always director-driven (no product to be
  // deterministic about) — hide the opt-in switch entirely.
  const showDirectorToggle = selections.campaignKind !== 'brand';

  const loadScaffold = async () => {
    if (!selections.campaignId) {
      toast({
        title: 'Pick a campaign first',
        description: 'The scaffold needs a campaign so it can resolve brand + format context.',
        status: 'warning',
        duration: 4000
      });
      return;
    }
    setScaffoldLoading(true);
    try {
      const qp = new URLSearchParams({
        campaignId: selections.campaignId,
        platformFormat: selections.platformFormat,
        durationSec: String(selections.videoDurationSec)
      });
      if (selections.productIds[0]) qp.set('productId', selections.productIds[0]);
      const resp = await apiJson<ScaffoldResp>(`/api/ads/veo-prompt-scaffold?${qp.toString()}`);
      onChange({ videoPromptRaw: resp.prompt || '' });
      setScaffoldMeta({
        model: resp.model,
        byteCap: resp.byteCap,
        approximation: resp.approximation ?? null
      });
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'unknown error';
      toast({ title: 'Scaffold load failed', description: msg, status: 'error', duration: 6000 });
    } finally {
      setScaffoldLoading(false);
    }
  };

  return (
    <Box borderWidth="1px" borderColor="brand.border" borderRadius="lg" bg="brand.surface" p={4}>
      <VStack align="stretch" spacing={4}>
        <HStack spacing={6} align="flex-start" wrap="wrap">
          <Box>
            <Text fontSize="11px" fontWeight="700" textTransform="uppercase" letterSpacing="0.06em" color="brand.muted" mb={2}>
              Video length
            </Text>
            <Select
              size="sm"
              maxW="200px"
              value={selections.videoDurationSec}
              onChange={(e) => onChange({ videoDurationSec: Number(e.target.value) })}
            >
              <option value={4}>4s</option>
              <option value={6}>6s</option>
              <option value={8}>8s</option>
              <option value={10}>10s (standard)</option>
              <option value={12}>12s</option>
              <option value={15}>15s</option>
            </Select>
            <Text fontSize="xs" color="brand.muted" mt={1}>
              Google Omni renders 4/6/8/10s — other lengths snap to the nearest. Grok renders any length.
            </Text>
          </Box>

          {showDirectorToggle && (
            <Box>
              <Text fontSize="11px" fontWeight="700" textTransform="uppercase" letterSpacing="0.06em" color="brand.muted" mb={2}>
                AI director variants
              </Text>
              <HStack spacing={3}>
                <Switch
                  size="md"
                  colorScheme="purple"
                  isChecked={selections.directorVariants}
                  onChange={(e) => onChange({ directorVariants: e.target.checked })}
                />
                <Text fontSize="sm" color="brand.ink" fontWeight="600">
                  {selections.directorVariants ? 'On' : 'Off'}
                </Text>
              </HStack>
              <Text fontSize="xs" color="brand.muted" mt={1} maxW="280px">
                Adds AI-directed video concepts on top of the standard product video.
              </Text>
            </Box>
          )}
        </HStack>

        <Box opacity={rawActive ? 0.45 : 1} pointerEvents={rawActive ? 'none' : 'auto'}>
          <Text fontSize="11px" fontWeight="700" textTransform="uppercase" letterSpacing="0.06em" color="brand.muted" mb={2}>
            Video prompt guidance
          </Text>
          <Textarea
            size="sm"
            rows={2}
            maxLength={1000}
            value={selections.videoPromptGuidance}
            onChange={(e) => onChange({ videoPromptGuidance: e.target.value })}
            placeholder="e.g. soft morning light, product held in hand, lifestyle kitchen setting"
            isDisabled={rawActive}
            bg="white"
          />
          <HStack justify="space-between" mt={1}>
            <Text fontSize="xs" color="brand.muted">
              {rawActive
                ? 'Disabled while a raw prompt is set (raw replaces the whole prompt).'
                : 'Extra creative direction merged into the standard prompt (mechanics preserved).'}
            </Text>
            <Text fontSize="xs" color="brand.muted">
              {selections.videoPromptGuidance.length}/1000
            </Text>
          </HStack>
        </Box>

        <Accordion allowToggle>
          <AccordionItem border="none">
            <AccordionButton
              px={0}
              py={1}
              _hover={{ bg: 'transparent' }}
              _expanded={{ color: 'rsViolet.700' }}
            >
              <Box flex="1" textAlign="left">
                <Text fontSize="11px" fontWeight="700" textTransform="uppercase" letterSpacing="0.06em" color="brand.muted">
                  Advanced — raw prompt
                </Text>
              </Box>
              <AccordionIcon color="brand.muted" />
            </AccordionButton>
            <AccordionPanel px={0} pb={1} pt={2}>
              <HStack justify="space-between" mb={2} wrap="wrap" spacing={2}>
                <Text fontSize="xs" color="orange.600" fontWeight="600" maxW="520px">
                  Raw replaces the entire prompt — the product-fidelity + no-text safeguards are bypassed. Applies to model-generated videos only.
                </Text>
                <Button
                  size="xs"
                  variant="outline"
                  onClick={loadScaffold}
                  isLoading={scaffoldLoading}
                  loadingText="Loading…"
                >
                  Load canonical scaffold
                </Button>
              </HStack>
              <Textarea
                size="sm"
                rows={6}
                maxLength={4000}
                value={selections.videoPromptRaw}
                onChange={(e) => onChange({ videoPromptRaw: e.target.value })}
                placeholder="Paste or edit the full Veo prompt. Leave empty to use the canonical prompt (+ guidance above)."
                fontFamily="mono"
                fontSize="11px"
                bg="white"
              />
              {/* APPROXIMATION NOTICE. Anyone debugging garbled output from this
                  box is reading a prompt that is NOT byte-for-byte what gets
                  submitted, and until now nothing said so. Rendered only once
                  the scaffold has actually been loaded, and only when the
                  backend declares it — so this is inert against a backend that
                  predates the field rather than asserting something unproven. */}
              {scaffoldMeta?.approximation?.isApproximation && (
                <VStack
                  align="stretch"
                  spacing={1}
                  mt={2}
                  bg="orange.50"
                  borderWidth="1px"
                  borderColor="orange.300"
                  borderRadius="md"
                  p={3}
                >
                  <Text fontSize="11px" fontWeight="700" color="orange.700" textTransform="uppercase" letterSpacing="0.06em">
                    Preview only — not byte-for-byte the submitted prompt
                  </Text>
                  <Text fontSize="sm" color="brand.ink">
                    {scaffoldMeta.approximation.reason}
                  </Text>
                  {!!scaffoldMeta.approximation.assumedInputs?.length && (
                    <VStack align="stretch" spacing={0.5} mt={1}>
                      {scaffoldMeta.approximation.assumedInputs.map(a => (
                        <Text key={a.field} fontSize="11px" color="brand.muted">
                          • <Text as="span" fontFamily="mono">{a.field}</Text> assumed{' '}
                          <Text as="span" fontFamily="mono">{JSON.stringify(a.assumed)}</Text> — {a.note}
                        </Text>
                      ))}
                    </VStack>
                  )}
                  {!!scaffoldMeta.approximation.omittedInputs?.length && (
                    <Text fontSize="11px" color="brand.muted">
                      • Not passed at all:{' '}
                      <Text as="span" fontFamily="mono">
                        {scaffoldMeta.approximation.omittedInputs.join(', ')}
                      </Text>
                    </Text>
                  )}
                  {scaffoldMeta.approximation.exactPromptAvailableAt && (
                    <Text fontSize="11px" color="brand.muted">
                      • The exact submitted prompt for a generated ad:{' '}
                      {scaffoldMeta.approximation.exactPromptAvailableAt}
                    </Text>
                  )}
                </VStack>
              )}
              <HStack justify="space-between" mt={1}>
                <Text fontSize="xs" color="brand.muted">
                  {scaffoldMeta
                    ? `Model: ${scaffoldMeta.model} · byte cap: ${scaffoldMeta.byteCap}`
                    : 'Load the scaffold to see model + byte-cap for this run.'}
                </Text>
                <Text fontSize="xs" color="brand.muted">
                  {selections.videoPromptRaw.length}/4000
                </Text>
              </HStack>
            </AccordionPanel>
          </AccordionItem>
        </Accordion>
      </VStack>
    </Box>
  );
}
